import { useEffect, useRef, useState } from 'react'
import { getSecret, setSecret, type ApiError } from './api'
import type { Rec, Section as Sec } from './model'
import { open, seal, vault } from './vault'
import { toast } from './toast'

const DOTS = '••••••••••'
const REVEAL_MS = 20_000

/**
 * The Password row in the detail panel (brief §3, §4).
 * Viewer: sees only whether a password is on file. Admin: reveal (step-up on the
 * master password, auto-hides after 20s / on blur), set, change, remove — one
 * record at a time.
 */
export function SecretRow({
  section,
  rec,
  canEdit,
  onChanged,
}: {
  section: Sec
  rec: Rec
  canEdit: boolean
  onChanged: () => void
}) {
  const hasPassword = !!rec.has_password
  const [revealed, setRevealed] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // auto-hide a revealed value
  useEffect(() => {
    if (revealed === null) return
    hideTimer.current = setTimeout(() => setRevealed(null), REVEAL_MS)
    const hideNow = () => setRevealed(null)
    const onVis = () => document.visibilityState === 'hidden' && hideNow()
    window.addEventListener('blur', hideNow)
    document.addEventListener('visibilitychange', onVis)
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current)
      window.removeEventListener('blur', hideNow)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [revealed])

  async function doReveal() {
    setBusy(true)
    try {
      const key = await vault.ensureKey()
      const { blob } = await getSecret(section.key, String(rec.id))
      if (!blob) {
        toast('No password on file')
        onChanged()
        return
      }
      setRevealed(await open(key, blob))
    } catch (e) {
      const msg = (e as ApiError | Error).message
      if (msg !== 'cancelled' && msg !== 'locked') toast(msg ?? 'Could not reveal')
    } finally {
      setBusy(false)
    }
  }

  async function saveDraft() {
    if (!draft) return
    setBusy(true)
    try {
      const key = await vault.ensureKey()
      const blob = await seal(key, draft)
      await setSecret(section.key, String(rec.id), blob)
      toast('Password saved')
      setEditing(false)
      setDraft('')
      onChanged()
    } catch (e) {
      const msg = (e as ApiError | Error).message
      if (msg !== 'cancelled' && msg !== 'locked') toast(msg ?? 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    setBusy(true)
    try {
      await setSecret(section.key, String(rec.id), null)
      toast('Password removed')
      setConfirmRemove(false)
      setRevealed(null)
      onChanged()
    } catch (e) {
      toast((e as ApiError).message ?? 'Could not remove')
    } finally {
      setBusy(false)
    }
  }

  // --- viewer ---------------------------------------------------------
  if (!canEdit) {
    return (
      <div className="kv">
        <span className="k">Password</span>
        <span className={`v${hasPassword ? '' : ' none'}`}>
          {hasPassword ? `${DOTS}  ·  on file` : 'No password on file'}
        </span>
      </div>
    )
  }

  // --- admin ---------------------------------------------------------
  return (
    <div className="kv secretkv">
      <span className="k">Password</span>
      <div className="secretbody">
        {editing ? (
          <div className="secretedit">
            <input
              type="text"
              autoFocus
              placeholder="Type the password"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && saveDraft()}
            />
            <button type="button" className="btn s p" disabled={busy || !draft} onClick={saveDraft}>
              Save
            </button>
            <button
              type="button"
              className="btn s"
              disabled={busy}
              onClick={() => {
                setEditing(false)
                setDraft('')
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            <code className={revealed !== null ? 'on' : ''}>
              {revealed !== null ? revealed : hasPassword ? DOTS : 'not stored'}
            </code>
            <div className="secretbtns">
              {hasPassword && revealed === null && (
                <button type="button" className="cp" disabled={busy} onClick={doReveal}>
                  reveal
                </button>
              )}
              {revealed !== null && (
                <>
                  <button
                    type="button"
                    className="cp"
                    onClick={() => {
                      navigator.clipboard?.writeText(revealed).then(() => toast('Copied'))
                    }}
                  >
                    copy
                  </button>
                  <button type="button" className="cp" onClick={() => setRevealed(null)}>
                    hide
                  </button>
                </>
              )}
              <button
                type="button"
                className="cp"
                disabled={busy}
                onClick={() => {
                  setEditing(true)
                  setDraft('')
                }}
              >
                {hasPassword ? 'change' : 'set'}
              </button>
              {hasPassword &&
                (confirmRemove ? (
                  <>
                    <button type="button" className="cp danger" disabled={busy} onClick={remove}>
                      really remove
                    </button>
                    <button type="button" className="cp" onClick={() => setConfirmRemove(false)}>
                      keep
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="cp danger"
                    disabled={busy}
                    onClick={() => setConfirmRemove(true)}
                  >
                    remove
                  </button>
                ))}
            </div>
            {revealed !== null && <p className="secrethint">Hides in 20 seconds, or when you leave the tab.</p>}
          </>
        )}
      </div>
    </div>
  )
}
